import { useMemo, useState } from "react";

import {
  useDirectTopupCategories,
  useDirectTopupMenus,
  useDirectTopupStock,
  type DirectTopupCard,
} from "@/api/menus";
import { FreshTile } from "@/components/FreshTile";
import { useT } from "@/lib/i18n";

interface CategoryHit {
  category_id: number;
  category_name: string;
  image_url: string | null;
  services_count: number;
  matched_service?: string;
}

/**
 * Customer-facing index of Direct Top-Up menus (mirror of ExploreFresh).
 * Empty search -> menu grid (browse mode). Non-empty search -> flat
 * list of categories whose name OR any nested service name matches
 * the query - same behaviour as the regular Explore page so the
 * customer can jump from "delta force" or "60 coins" straight to the
 * buy form without picking the right menu first.
 */
export function DirectTopup() {
  const menus = useDirectTopupMenus();
  const stock = useDirectTopupStock();
  // Flat category list lets us surface ungrouped DT categories under
  // an "Other" section in browse mode.
  const categories = useDirectTopupCategories();
  const t = useT();
  const [q, setQ] = useState("");

  const trimmed = q.trim().toLowerCase();
  const isSearching = trimmed.length > 0;

  const ungrouped: DirectTopupCard[] = useMemo(() => {
    const all = categories.data?.categories ?? [];
    return all.filter((c) => c.menu_id === null);
  }, [categories.data]);

  const categoryHits: CategoryHit[] = useMemo(() => {
    if (!isSearching || !stock.data) return [];
    const out: CategoryHit[] = [];
    for (const c of stock.data.categories) {
      const nameMatch = c.category_name.toLowerCase().includes(trimmed);
      const matchedService = !nameMatch
        ? c.services.find((s) =>
            s.service_name.toLowerCase().includes(trimmed),
          )
        : undefined;
      if (!nameMatch && !matchedService) continue;
      out.push({
        category_id: c.category_id,
        category_name: c.category_name,
        image_url: c.image_url,
        services_count: c.services.length,
        matched_service: matchedService?.service_name,
      });
    }
    return out;
  }, [isSearching, stock.data, trimmed]);

  return (
    <div>
      <h1
        style={{
          fontSize: 26,
          fontWeight: 700,
          color: "var(--color-text)",
          letterSpacing: "-0.01em",
        }}
      >
        {t("direct_topup.title")}
      </h1>
      <p
        style={{
          color: "var(--color-text-muted)",
          marginTop: 4,
          marginBottom: 20,
        }}
      >
        {t("direct_topup.subtitle")}
      </p>

      <div className="fresh-search-bar">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          style={{ color: "var(--color-text-muted)" }}
        >
          <circle cx="11" cy="11" r="7" />
          <path d="m20 20-3.5-3.5" />
        </svg>
        <input
          type="search"
          placeholder={t("direct_topup.search")}
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
        <span style={{ fontSize: 12, color: "var(--color-text-muted)" }}>
          {isSearching
            ? `${categoryHits.length.toLocaleString()} ${
                categoryHits.length === 1
                  ? t("explore.categories.one")
                  : t("explore.categories.many")
              }`
            : `${(menus.data?.menus ?? []).length.toLocaleString()} ${
                (menus.data?.menus ?? []).length === 1
                  ? t("explore.menus.one")
                  : t("explore.menus.many")
              }`}
        </span>
      </div>

      {(isSearching ? stock.isLoading : menus.isLoading) && (
        <div style={{ fontSize: 13, color: "var(--color-text-muted)" }}>
          {t("common.loading")}
        </div>
      )}
      {(isSearching ? stock.isError : menus.isError) && (
        <div
          style={{
            padding: 12,
            borderRadius: 8,
            background: "#fee2e2",
            color: "#b91c1c",
            fontSize: 13,
          }}
        >
          {t("explore.failed")}
        </div>
      )}

      {/* Browse mode: menu grid + ungrouped Other section */}
      {!isSearching && menus.data && (
        <>
          {(menus.data.menus.length ?? 0) > 0 && (
            <div className="fresh-tile-grid">
              {menus.data.menus.map((m) => (
                <FreshTile
                  key={m.id}
                  to={`/direct-topup/menus/${m.id}`}
                  title={m.name}
                  subtitle={`${m.categories_count} ${
                    m.categories_count === 1
                      ? t("explore.categories.one")
                      : t("explore.categories.many")
                  }`}
                  imageUrl={m.image_url}
                />
              ))}
            </div>
          )}

          {ungrouped.length > 0 && (
            <section style={{ marginTop: 28 }}>
              <h2
                style={{
                  fontSize: 18,
                  fontWeight: 600,
                  color: "var(--color-text)",
                  marginBottom: 12,
                }}
              >
                Other
              </h2>
              <div className="fresh-tile-grid">
                {ungrouped.map((c) => (
                  <FreshTile
                    key={c.category_id}
                    to={`/direct-topup/categories/${c.category_id}`}
                    title={c.name}
                    subtitle={`${c.services_count} ${
                      c.services_count === 1
                        ? t("explore.services.one")
                        : t("explore.services.many")
                    }`}
                    imageUrl={c.image_url}
                  />
                ))}
              </div>
            </section>
          )}

          {menus.data.menus.length === 0 && ungrouped.length === 0 && (
            <div
              style={{
                padding: 32,
                textAlign: "center",
                fontSize: 13,
                color: "var(--color-text-muted)",
              }}
            >
              {t("direct_topup.empty")}
            </div>
          )}
        </>
      )}

      {/* Search mode: category hits across DT */}
      {isSearching && stock.data && categoryHits.length > 0 && (
        <div className="fresh-tile-grid">
          {categoryHits.map((c) => (
            <FreshTile
              key={c.category_id}
              to={`/direct-topup/categories/${c.category_id}`}
              title={c.category_name}
              subtitle={
                c.matched_service
                  ? `${t("explore.match")}: ${c.matched_service}`
                  : `${c.services_count} ${
                      c.services_count === 1
                        ? t("explore.services.one")
                        : t("explore.services.many")
                    }`
              }
              imageUrl={c.image_url}
            />
          ))}
        </div>
      )}

      {isSearching && stock.data && categoryHits.length === 0 && (
        <div
          style={{
            padding: 32,
            textAlign: "center",
            fontSize: 13,
            color: "var(--color-text-muted)",
          }}
        >
          {t("explore.empty")}
        </div>
      )}
    </div>
  );
}
